import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Link,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import { postCompanyCreate } from "../services/callApi";
import {
  EXAM_PASSWORD_DUPLICATED_MESSAGE,
  getApiStatus,
  isExamPasswordDuplicatedError,
} from "../utils/managementApiError";
import {
  buildCompanyCreatePayload,
  getCompanyCreateErrorMessage,
  validateCompanyCreateForm,
} from "../utils/companyCreate";

const INITIAL_FORM_DATA = {
  companyName: "",
  examPassword: "",
};

function CompanyCreate({ role, onBack, onCreated }) {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errorMessage, setErrorMessage] = useState("");
  // 試験用パスワードの重複はパスワード欄の直下に出すため、上部Alertとは別に持つ。
  const [examPasswordError, setExamPasswordError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);
  const [isForbidden, setIsForbidden] = useState(false);
  const cannotCreate = role !== "super_admin" || isForbidden;

  const handleChange = (name) => (event) => {
    setFormData((current) => ({ ...current, [name]: event.target.value }));
    setErrorMessage("");
    if (name === "examPassword") {
      setExamPasswordError("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setExamPasswordError("");

    if (role !== "super_admin") {
      setIsForbidden(true);
      setErrorMessage("会社を登録する権限がありません。");
      return;
    }

    const validationMessage = validateCompanyCreateForm(formData);
    if (validationMessage) {
      setErrorMessage(validationMessage);
      return;
    }

    if (submitInFlightRef.current) {
      return;
    }

    submitInFlightRef.current = true;
    setIsSubmitting(true);
    try {
      const response = await postCompanyCreate(
        buildCompanyCreatePayload(formData)
      );

      if (response?.status !== 201) {
        setErrorMessage("会社の登録に失敗しました。時間を置いて再試行してください。");
        submitInFlightRef.current = false;
        setIsSubmitting(false);
        return;
      }

      // 成功後は試験パスワードを保持せず、一覧の再取得へ進む。
      setFormData(INITIAL_FORM_DATA);
      await onCreated(response.data);
    } catch (error) {
      const status = getApiStatus(error);
      if (status === 401) {
        window.location.href = "/login";
        return;
      }
      if (status === 403) {
        setIsForbidden(true);
      }
      if (isExamPasswordDuplicatedError(error)) {
        // 該当項目が分かるよう、上部Alertではなくパスワード欄の直下へ出す。
        setExamPasswordError(EXAM_PASSWORD_DUPLICATED_MESSAGE);
      } else {
        setErrorMessage(getCompanyCreateErrorMessage(error));
      }
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 0, sm: 1 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
          mb: 2,
        }}
      >
        <Typography variant="h5" component="h1">
          会社新規登録
        </Typography>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            underline="hover"
            color="inherit"
            sx={{ cursor: "pointer" }}
            onClick={(event) => {
              event.preventDefault();
              onBack();
            }}
          >
            会社一覧
          </Link>
          <Typography color="text.primary">新規登録</Typography>
        </Breadcrumbs>
      </Box>

      {cannotCreate && !errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          会社を登録する権限がありません。
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {!cannotCreate && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }} elevation={3}>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              required
              label="会社名"
              value={formData.companyName}
              onChange={handleChange("companyName")}
              disabled={isSubmitting}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              required
              type="password"
              autoComplete="new-password"
              label="試験用パスワード"
              value={formData.examPassword}
              onChange={handleChange("examPassword")}
              disabled={isSubmitting}
              error={!!examPasswordError}
              helperText={examPasswordError}
              sx={{ mb: 3 }}
            />
            <Box sx={{ textAlign: "center" }}>
              <Button
                variant="contained"
                type="submit"
                startIcon={<AddBusinessIcon />}
                disabled={isSubmitting}
              >
                登録
              </Button>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
}

export default CompanyCreate;
